/**
 * Catálogo do frontend da Gestão de Pedidos (`/pedidos`).
 *
 * Espelho pt-BR das 31 tags AUTOMÁTICAS de
 * `backend_v2/src/modules/pedidos/pedidoTags.js` (catálogo v3, 2026-09-23 —
 * rodada 4: processo de devolução, abaixo em `PEDIDO_TAG_META`; rodada 5:
 * `caso_para_conciliar` substitui `reembolso_para_conciliar`; rodada 7:
 * chegada ao CD, retirada da devolução na agência e NF-e de devolução;
 * rodada 8: o SUPERVISOR define o destino da devolução inspecionada; rodada 5
 * da Gestão de Pedidos, 2026-09-29: triagem do caso, apoio do financeiro e
 * pendência financeira). A
 * `key` é o contrato wire (snake_case) — o backend calcula a tag, o front só
 * traduz e colore. Tag fora do catálogo aparece com a key crua de propósito: é
 * sinal de tag nova no backend que precisa entrar aqui, não de erro silencioso.
 * Keys antigas (v2) saíram SEM alias.
 *
 * Toda tag tem um `kind`: `acao` (a vez é nossa) ou `acompanhamento` (estado
 * de outra parte — comprador, ML, transportadora). O `kind` agrupa e ordena;
 * a URGÊNCIA (vermelho) vem SÓ de `tags[].urgent` no wire — ação com prazo
 * curto, regra do backend — e o front nunca a deriva do `kind`.
 *
 * Linguagem visual do módulo (decisão do dono, 2026-09-22): cor com
 * significado por SETOR — sky = Atendimento/ML, amber = Logística/galpão,
 * cyan = Financeiro; red = urgente; gray = neutro/concluído. Nada arredondado.
 */
import { CurrencyDollar, EnvelopeSimple, Package } from 'shared/icons';
import mercadoLivreLogo from '../assets/channels/mercadolivre.png';
import mercadoLivreMark from '../assets/channels/mercadolivre-mark.png';
import { ORDER_STATUS_PT } from './paymentVocabulario';

/** Ordem canônica dos setores (blocos do menu, chips, filtros). */
export const SECTOR_ORDER = ['atendimento', 'logistica', 'financeiro'];

/**
 * Classes tailwind por TOM — plano (cantos quadrados), pares claro/escuro.
 *   chip    → fundo + texto do chip
 *   ring    → contorno discreto do chip (não urgente)
 *   border  → borda ESQUERDA da linha (`border-l-4` fica no componente)
 *   text    → texto colorido (sublinha dos blocos, rótulos)
 *   dot     → quadradinho de cor
 */
export const TONE_CLASSES = {
    sky: {
        chip: 'bg-sky-50 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300',
        ring: 'ring-1 ring-inset ring-sky-600/25 dark:ring-sky-400/30',
        border: 'border-l-sky-500',
        text: 'text-sky-700 dark:text-sky-300',
        dot: 'bg-sky-500',
    },
    amber: {
        chip: 'bg-amber-50 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300',
        ring: 'ring-1 ring-inset ring-amber-600/25 dark:ring-amber-400/30',
        border: 'border-l-amber-500',
        text: 'text-amber-800 dark:text-amber-300',
        dot: 'bg-amber-500',
    },
    cyan: {
        chip: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-300',
        ring: 'ring-1 ring-inset ring-cyan-600/25 dark:ring-cyan-400/30',
        border: 'border-l-cyan-600',
        text: 'text-cyan-700 dark:text-cyan-300',
        dot: 'bg-cyan-600',
    },
    red: {
        chip: 'bg-danger-50 text-danger-700 dark:bg-danger-400/10 dark:text-danger-300',
        ring: 'ring-1 ring-inset ring-danger-600/25 dark:ring-danger-400/30',
        border: 'border-l-danger-600',
        text: 'text-danger-600 dark:text-danger-400',
        dot: 'bg-danger-600',
    },
    emerald: {
        chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
        ring: 'ring-1 ring-inset ring-emerald-600/25 dark:ring-emerald-400/30',
        border: 'border-l-emerald-500',
        text: 'text-emerald-700 dark:text-emerald-400',
        dot: 'bg-emerald-500',
    },
    neutral: {
        chip: 'bg-mono-100 text-mono-500 dark:bg-mono-900 dark:text-mono-400',
        ring: 'ring-1 ring-inset ring-mono-400/20 dark:ring-mono-600/30',
        border: 'border-l-mono-300 dark:border-l-mono-700',
        text: 'text-mono-500 dark:text-mono-400',
        dot: 'bg-mono-400',
    },
};

/** Contorno do chip URGENTE — borda red sólida, independente do setor. */
export const URGENT_RING_CLASSES = 'ring-1 ring-inset ring-danger-600 dark:ring-danger-400';

/** Setores (decisão do dono: SOMENTE estes três). */
export const SECTOR_META = {
    atendimento: { label: 'Atendimento', tone: 'sky', Icon: EnvelopeSimple },
    logistica: { label: 'Logística', tone: 'amber', Icon: Package },
    financeiro: { label: 'Financeiro', tone: 'cyan', Icon: CurrencyDollar },
};

/** Tipos de tag (v3), na ordem de exibição: ação primeiro. */
export const TAG_KINDS = ['acao', 'acompanhamento'];

/**
 * Meta dos tipos — rótulo dos grupos de chips ("Para fazer" / "Acompanhar").
 * A urgência NÃO é do tipo (ver cabeçalho): é `urgent` do wire.
 */
export const TAG_KIND_META = {
    acao: { label: 'Para fazer', title: 'A vez é nossa' },
    acompanhamento: { label: 'Acompanhar', title: 'Estado de outra parte (comprador, ML, transportadora)' },
};

/**
 * As 31 tags automáticas (catálogo v3 + rodadas 4, 7 e 8 + triagem de
 * 2026-09-29) — MESMAS keys do
 * backend, ação antes de acompanhamento dentro de cada setor. `tone` = tom do
 * setor (a cor diz de quem é a pendência). Sem `urgent` estático: urgência é
 * do wire.
 *
 * Processo de devolução (decisão do Thiago, 2026-09-23): a LOGÍSTICA recebe a
 * devolução no galpão, imprime uma etiqueta e cola nela
 * (`devolucao_para_etiquetar`); o ATENDIMENTO inspeciona toda devolução com
 * etiqueta (`devolucao_para_inspecionar`) e dá o veredito ao ML
 * (`devolucao_para_revisar`, parte da inspeção). Destinar continua na
 * Logística.
 *
 * Rodada 7 (decisões do Thiago, 2026-09-23):
 *   · CHEGADA AO CD — devolução só vira pendência depois de chegar; a que
 *     nunca chegou (`devolucao_nao_chegou`) passou a ACOMPANHAMENTO.
 *   · RETIRADA — "quero ver retirada de DEVOLUÇÃO, não de vendas": sai a
 *     `retirada_nos_correios` (vendas, sem substituto) e entra
 *     `devolucao_para_retirar` (ação da Logística: a devolução está parada
 *     numa agência dos Correios/ML esperando NÓS irmos buscar).
 *   · FISCAL — sai `nf_devolucao_para_emitir` (NF ao FORNECEDOR: é da tela
 *     de RMAs, não da Gestão de Pedidos) e entra `nfe_devolucao_para_emitir`
 *     (devolução para nós sem NF-e de devolução emitida pelo ML nem marcada
 *     por nós). `nf_perda_para_emitir` passou a ser "chegou errado".
 *
 * Rodada 8 (ajuste 2 do dono, 2026-09-23): a inspeção termina no checklist;
 * quem define o destino (estoque, RMA ou descarte) é o SUPERVISOR
 * (`rmas:resolve`) — `devolucao_para_definir_destino` — e só depois a
 * Logística executa (`devolucao_para_destinar`, com o destino no rótulo).
 *
 * `requires` (opcional) = permissão sem a qual o backend NÃO manda a tag
 * (nem na linha, nem no summary, nem no filtro). O front não filtra por
 * ela: mostra só o que o wire trouxer — o campo documenta a regra do
 * servidor.
 */
export const PEDIDO_TAG_META = {
    // — Atendimento —
    mensagem_para_responder: { label: 'Mensagem para responder', sector: 'atendimento', kind: 'acao', tone: 'sky' },
    reclamacao_para_responder: {
        label: 'Reclamação para responder',
        sector: 'atendimento',
        kind: 'acao',
        tone: 'sky',
    },
    mediacao_para_responder: { label: 'Mediação para responder', sector: 'atendimento', kind: 'acao', tone: 'sky' },
    recontato_para_responder: { label: 'Recontato para responder', sector: 'atendimento', kind: 'acao', tone: 'sky' },
    devolucao_para_inspecionar: {
        label: 'Devolução para inspecionar',
        sector: 'logistica',
        kind: 'acao',
        tone: 'amber',
    },
    devolucao_para_revisar: {
        label: 'Devolução para revisar (veredito ML)',
        sector: 'atendimento',
        kind: 'acao',
        tone: 'sky',
    },
    // Rodada 5 (2026-09-29): triagem do caso → decisão do supervisor → apoio
    // do financeiro. Só casos elegíveis (fora cancelamento de compra; abertos a
    // partir de `triagem_data_corte`). Unidade = caso (rma).
    caso_para_triar: { label: 'Caso para triar', sector: 'atendimento', kind: 'acao', tone: 'sky' },
    caso_para_decidir: {
        label: 'Caso para decidir',
        sector: 'atendimento',
        kind: 'acao',
        tone: 'sky',
        requires: 'rmas:resolve',
    },
    caso_nao_resolvido: { label: 'Caso não resolvido', sector: 'atendimento', kind: 'acao', tone: 'sky' },
    reclamacao_em_andamento: {
        label: 'Reclamação em andamento',
        sector: 'atendimento',
        kind: 'acompanhamento',
        tone: 'sky',
    },
    mediacao_em_andamento: {
        label: 'Mediação em andamento',
        sector: 'atendimento',
        kind: 'acompanhamento',
        tone: 'sky',
    },
    caso_com_ml: { label: 'Caso em análise pelo ML', sector: 'atendimento', kind: 'acompanhamento', tone: 'sky' },
    recontato_com_ml: {
        label: 'Recontato em análise pelo ML',
        sector: 'atendimento',
        kind: 'acompanhamento',
        tone: 'sky',
    },
    mensagens_bloqueadas: {
        label: 'Mensagens bloqueadas pela reclamação',
        sector: 'atendimento',
        kind: 'acompanhamento',
        tone: 'sky',
    },
    // Rodada 5: o supervisor pediu apoio ao financeiro e ele ainda não respondeu.
    caso_aguardando_financeiro: {
        label: 'Aguardando financeiro',
        sector: 'atendimento',
        kind: 'acompanhamento',
        tone: 'sky',
    },
    // — Logística —
    // Retirada (rodada 7): a devolução está parada numa agência esperando a
    // NOSSA retirada — ação que traz a mercadoria ao CD (não exige chegada).
    devolucao_para_retirar: {
        label: 'Devolução para retirar nos Correios',
        sector: 'logistica',
        kind: 'acao',
        tone: 'amber',
    },
    devolucao_para_etiquetar: {
        label: 'Devolução para etiquetar',
        sector: 'logistica',
        kind: 'acao',
        tone: 'amber',
    },
    devolucao_para_triagem: {
        label: 'Devolução para triagem',
        sector: 'logistica',
        kind: 'acao',
        tone: 'amber',
    },
    // Rodada 8: devolução INSPECIONADA ainda sem destino — só o supervisor
    // (`rmas:resolve`) vê e conta; ele define estoque/RMA/descarte no caso.
    devolucao_para_definir_destino: {
        label: 'Devolução inspecionada — definir destino',
        sector: 'logistica',
        kind: 'acao',
        tone: 'amber',
        requires: 'rmas:resolve',
    },
    // Destino definido, falta EXECUTAR — o chip da linha diz o destino
    // ("Devolução para destinar: estoque"; `TagWireLabel`).
    devolucao_para_destinar: { label: 'Devolução para destinar', sector: 'logistica', kind: 'acao', tone: 'amber' },
    envio_para_despachar: { label: 'Envio para despachar', sector: 'logistica', kind: 'acao', tone: 'amber' },
    devolucao_aguardando_postagem: {
        label: 'Devolução aguardando postagem do comprador',
        sector: 'logistica',
        kind: 'acompanhamento',
        tone: 'amber',
    },
    devolucao_a_caminho: { label: 'Devolução a caminho', sector: 'logistica', kind: 'acompanhamento', tone: 'amber' },
    // Rodada 7: acompanhamento (não é pendência nem urgente; sem SLA).
    devolucao_nao_chegou: {
        label: 'Devolução não chegou',
        sector: 'logistica',
        kind: 'acompanhamento',
        tone: 'amber',
    },
    envio_nao_entregue: { label: 'Envio não entregue', sector: 'logistica', kind: 'acompanhamento', tone: 'amber' },
    // — Financeiro —
    // Rodada 5 (decisão do Thiago, 2026-09-23): TODO caso do ML encerrado
    // (reclamação, mediação, devolução, cancelamento; Full também) gera um
    // "para fazer" do Financeiro até ser marcado conciliado/verificado — não só
    // os reembolsados. Substitui `reembolso_para_conciliar` (sem alias). Só
    // conta caso encerrado a partir da data de corte (`conciliacao_data_corte`).
    caso_para_conciliar: { label: 'Verificar caso', sector: 'financeiro', kind: 'acao', tone: 'cyan' },
    // Rodada 7: devolução para NÓS sem NF-e de devolução emitida pelo ML nem
    // marcada por nós (só acende depois da 1ª checagem da NF no ML).
    nfe_devolucao_para_emitir: {
        label: 'NF-e de devolução para emitir',
        sector: 'financeiro',
        kind: 'acao',
        tone: 'cyan',
    },
    // Rodada 7: "chegou ERRADO" — inspeção com problema e sem NF de perda.
    nf_perda_para_emitir: { label: 'NF de perda para emitir', sector: 'financeiro', kind: 'acao', tone: 'cyan' },
    pagamento_para_conferir: { label: 'Pagamento para conferir', sector: 'financeiro', kind: 'acao', tone: 'cyan' },
    // Rodada 5: pedido de apoio do supervisor no caso (o `detail` do chip traz
    // o pedido) e a pendência marcada no status do financeiro do pedido (o
    // `detail` traz a nota) — `TagSupportTitleParts`.
    apoio_financeiro_solicitado: { label: 'Apoio do financeiro', sector: 'financeiro', kind: 'acao', tone: 'cyan' },
    pendencia_financeira: { label: 'Pendência financeira', sector: 'financeiro', kind: 'acao', tone: 'cyan' },
    recebimento_pendente: { label: 'Recebimento pendente', sector: 'financeiro', kind: 'acompanhamento', tone: 'cyan' },
};

/** Keys na ordem do catálogo (setor a setor, ação antes de acompanhamento). */
export const PEDIDO_TAG_KEYS = Object.keys(PEDIDO_TAG_META);

/** A key está no catálogo do front? (URL antiga com key v2 → false.) */
export function IsKnownTagKey(key) {
    return Object.hasOwn(PEDIDO_TAG_META, String(key || ''));
}

/** Rótulo pt-BR da tag; key desconhecida volta crua (nunca escondida). */
export function TagLabel(key) {
    return PEDIDO_TAG_META[key]?.label || String(key || '');
}

/** Keys das tags de um setor, na ordem do catálogo; `kind` filtra o tipo. */
export function TagKeysBySector(sector, kind = null) {
    return PEDIDO_TAG_KEYS.filter(
        (key) => PEDIDO_TAG_META[key].sector === sector && (!kind || PEDIDO_TAG_META[key].kind === kind),
    );
}

/**
 * Tipo da tag: `kind` do wire manda; sem ele, o catálogo pela key; fora dos
 * dois, null. Aceita o objeto do wire (`{ key, kind, … }`) ou a key solta.
 */
export function TagKind(tag) {
    if (tag && typeof tag === 'object') {
        if (TAG_KINDS.includes(tag.kind)) {
            return tag.kind;
        }
        return PEDIDO_TAG_META[tag.key]?.kind || null;
    }
    return PEDIDO_TAG_META[tag]?.kind || null;
}

/** Tag de AÇÃO (a vez é nossa): agrupa e ordena — NÃO pinta de vermelho. */
export function IsActionTag(tag) {
    return TagKind(tag) === 'acao';
}

/**
 * Tag URGENTE (vermelho): SÓ `urgent` do wire (ação com prazo curto, regra
 * do backend). Key solta nunca é urgente — o front não deriva urgência.
 */
export function IsUrgentTag(tag) {
    return Boolean(tag && typeof tag === 'object' && tag.urgent);
}

// ── Destino da devolução inspecionada (rodada 8 — ajuste 2 do dono) ─────────

/** Tag de ação do supervisor: devolução inspecionada ainda SEM destino. */
export const DEFINE_DESTINATION_TAG_KEY = 'devolucao_para_definir_destino';

/** Tag de ação da Logística: destino definido pelo supervisor, falta executar. */
export const DESTINATION_TAG_KEY = 'devolucao_para_destinar';

/**
 * Destinos possíveis (só três, por enquanto) — `rmas.inspection_destination`,
 * gravado pelo supervisor (`POST /rmas/:id/destination`). `inline` é a forma
 * que entra no rótulo do chip ("Devolução para destinar: estoque").
 */
export const INSPECTION_DESTINATION_META = {
    ESTOQUE: { label: 'Estoque', inline: 'estoque' },
    RMA: { label: 'RMA', inline: 'RMA' },
    DESCARTE: { label: 'Descarte', inline: 'descarte' },
};

/** Destinos na ordem dos botões do supervisor. */
export const INSPECTION_DESTINATIONS = Object.keys(INSPECTION_DESTINATION_META);

/** "estoque / RMA / descarte" — as opções por extenso (title, textos de ajuda). */
export const INSPECTION_DESTINATIONS_TEXT = INSPECTION_DESTINATIONS.map(
    (key) => INSPECTION_DESTINATION_META[key].inline,
).join(' / ');

/** Meta do destino; valor desconhecido volta cru (nunca escondido); vazio → null. */
export function InspectionDestinationMeta(value) {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return null;
    }
    return INSPECTION_DESTINATION_META[raw.toUpperCase()] || { label: raw, inline: raw };
}

/**
 * Destino do chip `devolucao_para_destinar` no wire — `detail.destination`
 * (mesmo mecanismo `detail` do chip da retirada: o caso que casa a fila).
 * Outra tag, key solta, sem `detail` (backend antigo) ou destino vazio → null.
 */
export function TagDestination(tag) {
    if (!tag || typeof tag !== 'object' || tag.key !== DESTINATION_TAG_KEY) {
        return null;
    }
    return InspectionDestinationMeta(tag.detail?.destination);
}

/**
 * Rótulo do chip a partir do objeto do wire (ou da key solta): o do catálogo
 * e, em `devolucao_para_destinar` com destino, "Devolução para destinar:
 * estoque" / "…: RMA" / "…: descarte". Sem destino no wire (fallback), o
 * rótulo do catálogo.
 */
export function TagWireLabel(tag) {
    const key = tag && typeof tag === 'object' ? tag.key : tag;
    const destination = TagDestination(tag);
    const label = TagLabel(key);
    return destination ? `${label}: ${destination.inline}` : label;
}

/**
 * Partes do `title` dos chips do destino: quem define e o que falta. Outras
 * tags → [].
 */
export function TagDestinationTitleParts(tag) {
    const key = tag && typeof tag === 'object' ? tag.key : tag;
    if (key === DEFINE_DESTINATION_TAG_KEY) {
        return [`o supervisor define o destino: ${INSPECTION_DESTINATIONS_TEXT}`];
    }
    if (key !== DESTINATION_TAG_KEY) {
        return [];
    }
    const destination = TagDestination(tag);
    return destination
        ? [`destino definido: ${destination.label} — falta executar`]
        : [`destino: ${INSPECTION_DESTINATIONS_TEXT}`];
}

// ── Origem (rodada 2, decisão 6): de onde a informação veio ──────────────────

/**
 * Selo DISCRETO de origem das tags e dos blocos: `ml` = derivada de dados do
 * Mercado Livre, `sistema` = nossa (manual, notas, inspeção, destino),
 * `bling` = reservado à integração. Classes planas e apagadas — o selo é
 * contexto, não manchete.
 */
export const ORIGIN_META = {
    ml: {
        label: 'ML',
        title: 'Derivada de dados do Mercado Livre',
        badge: 'bg-sky-600/10 text-sky-800 dark:bg-sky-300/10 dark:text-sky-200',
    },
    sistema: {
        label: 'Sistema',
        title: 'Registrada aqui, pela equipe ou pelo sistema',
        badge: 'bg-mono-900/10 text-mono-700 dark:bg-white/10 dark:text-mono-200',
    },
    bling: {
        label: 'Bling',
        title: 'Integração Bling (em preparação)',
        badge: 'bg-emerald-600/10 text-emerald-800 dark:bg-emerald-300/10 dark:text-emerald-200',
    },
};

/** Meta da origem; origem desconhecida aparece crua com o selo neutro. */
export function OriginMeta(origin) {
    if (!origin) {
        return null;
    }
    return ORIGIN_META[origin] || { label: String(origin), title: String(origin), badge: ORIGIN_META.sistema.badge };
}

// ── Identidade multi-canal (rodada 2, decisão 6; logo do canal na rodada 3) ──

/**
 * Canais de venda (`order.channel`) — só o ML existe hoje; shopee/magalu
 * ficam preparados com rótulo e sem asset. `mark` = ícone quadrado (célula
 * estreita da lista); `logo` = logo completa (cabeçalho do detalhe). Os dois
 * são assets LOCAIS empacotados pelo build — hotlink do mlstatic é bloqueado
 * pelo navegador (`utils/ml.js`). `short` é o texto curto que `ChannelLogo`
 * escreve quando o canal não tem asset.
 */
export const CHANNEL_META = {
    mercadolivre: { label: 'Mercado Livre', short: 'ML', mark: mercadoLivreMark, logo: mercadoLivreLogo },
    shopee: { label: 'Shopee', short: 'Shopee', mark: null, logo: null },
    magalu: { label: 'Magalu', short: 'Magalu', mark: null, logo: null },
};

/** Canal → rótulo (derivado de `CHANNEL_META`; mantido para os consumidores). */
export const CHANNEL_LABELS = Object.fromEntries(
    Object.entries(CHANNEL_META).map(([channel, meta]) => [channel, meta.label]),
);

/** Rótulo do canal; desconhecido volta cru, vazio vira '—'. */
export function ChannelLabel(channel) {
    if (!channel) {
        return '—';
    }
    return CHANNEL_LABELS[channel] || String(channel);
}

/**
 * Meta do canal: desconhecido volta com o nome cru como rótulo/short e sem
 * asset (nunca escondido); vazio → null.
 */
export function ChannelMeta(channel) {
    if (!channel) {
        return null;
    }
    const key = String(channel);
    return CHANNEL_META[key] || { label: key, short: key, mark: null, logo: null };
}

// ── Conversa pós-venda (rodada 2, decisão 1) ─────────────────────────────────

/**
 * Estado da conversa do pack calculado pelo backend (`conversation.state`),
 * fiel ao ML: quem tem a bola e se a mensageria está bloqueada.
 *   falta_nossa_resposta     → ativa, última do comprador (urgente)
 *   aguardando_comprador     → ativa, última nossa
 *   bloqueada_por_reclamacao → ML bloqueou por reclamação aberta (responder lá)
 *   bloqueada                → bloqueada por outro motivo (sem compositor)
 *   sem_mensagens            → existe no ML, mas vazia
 */
export const CONVERSATION_STATE_META = {
    falta_nossa_resposta: { label: 'Falta nossa resposta', tone: 'red', urgent: true },
    aguardando_comprador: { label: 'Aguardando o comprador', tone: 'emerald', urgent: false },
    bloqueada_por_reclamacao: { label: 'Bloqueada por reclamação', tone: 'amber', urgent: true },
    bloqueada: { label: 'Bloqueada', tone: 'neutral', urgent: false },
    sem_mensagens: { label: 'Sem mensagens', tone: 'neutral', urgent: false },
};

/** Meta do estado da conversa; estado desconhecido volta cru, neutro. */
export function ConversationStateMeta(state) {
    return CONVERSATION_STATE_META[state] || { label: String(state || '—'), tone: 'neutral', urgent: false };
}

/**
 * "Vez de:" na reclamação/mediação (`ocorrencia.turn`): `nossa` = respondent,
 * `comprador` = complainant, `ml` = processo interno do Mercado Livre (aberta
 * sem responsável ou sem ações disponíveis).
 */
export const TURN_META = {
    nossa: { label: 'nossa', tone: 'red' },
    comprador: { label: 'do comprador', tone: 'emerald' },
    ml: { label: 'do Mercado Livre', tone: 'neutral' },
};

/** Meta da vez; valor desconhecido volta cru, neutro; vazio → null. */
export function TurnMeta(turn) {
    if (!turn) {
        return null;
    }
    return TURN_META[turn] || { label: String(turn), tone: 'neutral' };
}

/**
 * Paleta FIXA das tags manuais (mesmos nomes aceitos pelo backend em
 * `order_tag_definitions.color`). `swatch` é o quadradinho de cor (picker e
 * chip); `chip` é fundo + texto + contorno do chip.
 */
export const MANUAL_TAG_COLORS = {
    gray: {
        label: 'Cinza',
        swatch: 'bg-mono-400',
        chip: 'bg-mono-100 text-mono-700 ring-1 ring-inset ring-mono-400/30 dark:bg-mono-900 dark:text-mono-300 dark:ring-mono-600/30',
    },
    red: {
        label: 'Vermelho',
        swatch: 'bg-danger-500',
        chip: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-600/25 dark:bg-danger-400/10 dark:text-danger-300 dark:ring-danger-400/30',
    },
    amber: {
        label: 'Âmbar',
        swatch: 'bg-amber-500',
        chip: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/25 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
    },
    emerald: {
        label: 'Verde',
        swatch: 'bg-emerald-500',
        chip: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/25 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
    },
    sky: {
        label: 'Azul',
        swatch: 'bg-sky-500',
        chip: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/25 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30',
    },
    violet: {
        label: 'Violeta',
        swatch: 'bg-violet-500',
        chip: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-600/25 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30',
    },
    cyan: {
        label: 'Ciano',
        swatch: 'bg-cyan-600',
        chip: 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/25 dark:bg-cyan-400/10 dark:text-cyan-300 dark:ring-cyan-400/30',
    },
    pink: {
        label: 'Rosa',
        swatch: 'bg-pink-500',
        chip: 'bg-pink-50 text-pink-700 ring-1 ring-inset ring-pink-600/25 dark:bg-pink-400/10 dark:text-pink-300 dark:ring-pink-400/30',
    },
};

export const MANUAL_TAG_COLOR_KEYS = Object.keys(MANUAL_TAG_COLORS);

/** Meta da cor de uma tag manual; cor desconhecida cai no cinza. */
export function ManualTagColor(color) {
    return MANUAL_TAG_COLORS[color] || MANUAL_TAG_COLORS.gray;
}

/**
 * Situação do PEDIDO no ML — rótulos reaproveitados de `paymentVocabulario`
 * (fonte única); aqui só entra a cor do pill. Valores levantados do banco
 * (paid · cancelled · partially_refunded · pending_cancel) + o vocabulário
 * oficial que ainda não apareceu.
 */
const PILL_OK =
    'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30';
const PILL_WARN =
    'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30';
const PILL_BAD =
    'bg-danger-50 text-danger-700 ring-danger-600/20 dark:bg-danger-400/10 dark:text-danger-300 dark:ring-danger-400/30';
const PILL_INFO = 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30';
export const PILL_NEUTRAL =
    'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30';

export const ORDER_STATUS_META = {
    paid: { label: ORDER_STATUS_PT.paid, pill: PILL_OK },
    cancelled: { label: ORDER_STATUS_PT.cancelled, pill: PILL_NEUTRAL },
    partially_refunded: { label: ORDER_STATUS_PT.partially_refunded, pill: PILL_WARN },
    pending_cancel: { label: ORDER_STATUS_PT.pending_cancel, pill: PILL_BAD },
    confirmed: { label: ORDER_STATUS_PT.confirmed, pill: PILL_INFO },
    payment_required: { label: ORDER_STATUS_PT.payment_required, pill: PILL_WARN },
    payment_in_process: { label: ORDER_STATUS_PT.payment_in_process, pill: PILL_WARN },
    partially_paid: { label: ORDER_STATUS_PT.partially_paid, pill: PILL_WARN },
    invalid: { label: ORDER_STATUS_PT.invalid, pill: PILL_BAD },
};

/** Opções do filtro de status da venda (valor cru no wire `?status=`). */
export const ORDER_STATUS_FILTERS = [
    { value: '', label: 'Todos os status' },
    ...Object.entries(ORDER_STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
];

// ── Regras da Gestão de Pedidos (`/pedidos/regras`, rodada 3b — 2026-09-23) ──
// O backend (`order_rules`, `GET/PUT /pedidos/regras`) é a fonte de verdade de
// valores, padrões, limites e textos; aqui só o que a PÁGINA precisa para
// montar a frase da Regra 1 e ligar cada SLA às tags que ele mede.
//
// Dois regimes de urgência por tag de ação (adendo do Thiago):
//   prazo do ML → urgente se vence até o corte da Regra 1 (`urgent_reason`
//                 'prazo_ml');
//   SLA interno → urgente quando passou do SLA sem resposta (`urgent_reason`
//                 'sla'), relógio corrido, em MINUTOS.

/**
 * Regra 1 — horário de atendimento e corte da urgência dos prazos do ML. As
 * quatro keys são editadas juntas, numa frase só: "Atendimento das [início]h
 * às [fim]h, dias [dias] — é urgente toda ação nossa com prazo do ML até as
 * [corte]h do próximo dia de atendimento". Início/fim são informativos; o
 * corte é o que vale.
 */
export const RULE_ONE_KEYS = [
    'atendimento_inicio_hora',
    'atendimento_fim_hora',
    'dias_atendimento',
    'urgencia_corte_hora',
];

/** Dias da semana em ISO 8601 (1 = segunda … 7 = domingo), como `dias_atendimento`. */
export const WEEKDAYS_ISO = [
    { iso: 1, short: 'seg', label: 'segunda' },
    { iso: 2, short: 'ter', label: 'terça' },
    { iso: 3, short: 'qua', label: 'quarta' },
    { iso: 4, short: 'qui', label: 'quinta' },
    { iso: 5, short: 'sex', label: 'sexta' },
    { iso: 6, short: 'sáb', label: 'sábado' },
    { iso: 7, short: 'dom', label: 'domingo' },
];

/**
 * SLA interno (minutos) → tags de ação que ele mede, na ordem da página.
 * Vazio = "sem SLA" (a tag só fica urgente pelo prazo do ML, se tiver).
 */
export const SLA_RULE_TAG_KEYS = {
    sla_mensagem_minutos: ['mensagem_para_responder'],
    sla_reclamacao_minutos: ['reclamacao_para_responder'],
    sla_mediacao_minutos: ['mediacao_para_responder'],
    sla_recontato_minutos: ['recontato_para_responder'],
    sla_revisao_devolucao_minutos: ['devolucao_para_revisar'],
    sla_etiquetar_minutos: ['devolucao_para_etiquetar'],
    sla_inspecao_minutos: ['devolucao_para_inspecionar', 'devolucao_para_triagem'],
    // Rodada 8: o mesmo SLA (contado da inspeção) cobre definir o destino
    // (supervisor) e executá-lo (Logística) — como no backend.
    sla_destinar_minutos: ['devolucao_para_definir_destino', 'devolucao_para_destinar'],
    // Rodada 7: retirada da devolução na agência (padrão 24 h, contado da
    // entrada no substatus); `sla_averiguar_minutos` saiu ("não chegou" virou
    // acompanhamento).
    sla_retirar_devolucao_minutos: ['devolucao_para_retirar'],
    sla_nfe_devolucao_minutos: ['nfe_devolucao_para_emitir'],
    // NF de perda: da solicitação fiscal, com fallback para a inspeção legada.
    sla_nf_minutos: ['nf_perda_para_emitir'],
    sla_pagamento_minutos: ['pagamento_para_conferir'],
    // Triagem (2026-09-29): da abertura do caso; da triagem concluída / da
    // resposta do financeiro; do pedido de apoio; da marcação de pendência.
    sla_triagem_minutos: ['caso_para_triar'],
    sla_decisao_minutos: ['caso_para_decidir'],
    sla_apoio_financeiro_minutos: ['apoio_financeiro_solicitado'],
    sla_pendencia_financeira_minutos: ['pendencia_financeira'],
};

/** Keys de SLA na ordem da página. */
export const SLA_RULE_KEYS = Object.keys(SLA_RULE_TAG_KEYS);

/**
 * Regras de DATA (`unit: 'date'` no wire, valor 'AAAA-MM-DD' ou null) → tags
 * que elas limitam. `conciliacao_data_corte` (rodada 5): só entram em
 * "Conciliar/verificar caso" os casos encerrados a partir desta data; vazio =
 * sem corte. Título e descrição vêm do backend.
 */
export const DATE_RULE_TAG_KEYS = {
    conciliacao_data_corte: ['caso_para_conciliar'],
    // Triagem (2026-09-29): só casos abertos a partir desta data entram.
    triagem_data_corte: ['caso_para_triar', 'caso_para_decidir', 'caso_nao_resolvido', 'caso_aguardando_financeiro'],
};

/** Texto fixo do campo da data de corte da conciliação (página de Regras). */
export const CONCILIATION_CUTOFF_HINT = 'Só entram na conciliação os casos encerrados a partir desta data.';

// ── Caso encerrado × conciliado/verificado (rodada 5) ────────────────────────

/** Status locais do caso que contam como ENCERRADO (além do claim fechado no ML). */
export const CLOSED_CASE_STATUSES = ['RESOLVIDO', 'CANCELADO'];

/**
 * Caso ENCERRADO — mesmo critério da tag `caso_para_conciliar`: o ML fechou o
 * claim (`ml_claim_status` 'closed') OU o desfecho local foi registrado
 * (status RESOLVIDO/CANCELADO). Aceita a ocorrência do pedido ou o caso do
 * `/rmas/:id`.
 */
export function IsCaseClosed(occ) {
    if (!occ || typeof occ !== 'object') {
        return false;
    }
    return occ.ml_claim_status === 'closed' || CLOSED_CASE_STATUSES.includes(occ.status);
}

/** Situações de reembolso que já moveram dinheiro (a conciliação antiga valia para estas). */
export const REFUNDED_CASE_STATUSES = ['REEMBOLSADO', 'PARCIAL'];

/**
 * Caso ainda NÃO conciliado/verificado que pede a ação "Marcar
 * conciliado/verificado": todo caso ENCERRADO (com ou sem reembolso — rodada
 * 5) e, como antes, o caso já REEMBOLSADO mesmo em aberto (a fila
 * conferir_reembolso do /rmas não mudou; sem isto o botão sumia do caso). A
 * data de corte só filtra a TAG, não a ação. `refund_reconciled_at` ausente
 * do wire (backend antigo) → false: sem o campo, a ação some.
 */
export function NeedsCaseReconcile(occ) {
    if (!occ || typeof occ !== 'object' || occ.reconciliation_not_applicable === true) {
        return false;
    }
    return (
        (IsCaseClosed(occ) || REFUNDED_CASE_STATUSES.includes(occ.refund_status)) && occ.refund_reconciled_at === null
    );
}

/** A key é de SLA interno (`sla_*_minutos`)? */
export function IsSlaRuleKey(key) {
    return /^sla_[a-z0-9_]+_minutos$/.test(String(key || ''));
}

/**
 * Regras do wire (`GET /pedidos/regras`) → `{ <tag key>: minutos }` só das
 * tags com SLA configurado (usado no title do chip: "SLA de 15 min").
 */
export function SlaMinutesByTag(rules = []) {
    const out = {};
    for (const rule of Array.isArray(rules) ? rules : []) {
        const minutes = Number(rule?.value);
        if (!IsSlaRuleKey(rule?.key) || rule?.value === null || !Number.isFinite(minutes) || minutes <= 0) {
            continue;
        }
        for (const tagKey of SLA_RULE_TAG_KEYS[rule.key] || []) {
            out[tagKey] = minutes;
        }
    }
    return out;
}

/** Motivo da urgência no wire (`tags[].urgent_reason`). */
export const URGENT_REASONS = ['prazo_ml', 'sla'];

/** Tags de ação cujo prazo vem do Mercado Livre (Regra 1; não editável). */
export const ML_DEADLINE_TAGS = [
    { key: 'reclamacao_para_responder', source: 'prazo de resposta da reclamação' },
    { key: 'mediacao_para_responder', source: 'prazo de resposta da mediação' },
    { key: 'recontato_para_responder', source: 'prazo de resposta do recontato' },
    { key: 'devolucao_para_revisar', source: 'prazo da revisão da devolução' },
    { key: 'envio_para_despachar', source: 'limite de despacho do envio' },
];

/**
 * Tags SÓ DE LINHA (backend `countable: false` — coluna sem índice): aparecem
 * no pedido, com prazo no chip, mas não entram em Urgentes, Para fazer, nas
 * contagens dos blocos nem nos filtros (o summary manda `null` para elas).
 */
export const ROW_ONLY_TAG_KEYS = [
    'envio_para_despachar',
    'envio_nao_entregue',
    'pagamento_para_conferir',
    'recebimento_pendente',
];

/** Texto da página de Regras para regra que só mede tags só de linha. */
export const ROW_ONLY_RULE_NOTE =
    'Só na linha: o prazo aparece no chip do pedido, mas esta tag não entra em Urgentes, Para fazer nem nas contagens.';

/**
 * Bloco da lista que mostra o botão "▮ Iniciar inspeção" (estação em tela
 * cheia). Rodada 4 (decisão do Thiago): quem inspeciona as devoluções
 * etiquetadas é o ATENDIMENTO — a Logística só recebe e etiqueta.
 */
export const INSPECTION_STATION_SECTOR = 'logistica';

// ── Chegada ao CD e inspeção (rodada 7 — decisão do Thiago, 2026-09-23) ──────

/**
 * "Somente quando uma mercadoria chegar no CD para conferência é que ela tem
 * que entrar para inspeção." A inspeção só aceita caso RECEBIDO (etiquetado
 * pela Logística = recebido); ABERTO/EM_TRANSITO → 422 com este código e
 * esta mensagem (RmaService.registerInspection). O front mostra o MESMO
 * texto ao bipar/abrir um caso ainda não recebido.
 */
export const INSPECTION_NOT_RECEIVED_CODE = 'RMA26_NOT_RECEIVED';
export const INSPECTION_NOT_RECEIVED_MESSAGE =
    'A devolução ainda não foi recebida pela Logística — ela precisa chegar ao CD e ser etiquetada antes da inspeção';

/** Status em que a inspeção é possível (só depois do recebimento). */
export const INSPECTABLE_STATUSES = ['RECEBIDO'];

/** Status do fluxo físico ANTES do recebimento (bipar/abrir → mensagem acima). */
export const NOT_RECEIVED_STATUSES = ['ABERTO', 'EM_TRANSITO'];

/** O caso pode ser inspecionado agora? (Só RECEBIDO.) */
export function IsInspectable(rma) {
    return Boolean(rma && INSPECTABLE_STATUSES.includes(rma.status));
}

/**
 * Caso SEM fluxo físico aqui (venda Full cuja devolução vai ao armazém do ML):
 * não há etiqueta nem inspeção no nosso CD. Full que volta ao NOSSO endereço
 * segue o fluxo normal (dono, 24/09) — ver HasPhysicalFlow em rmaShared.
 */
export const FULL_NO_RECEIPT_MESSAGE = 'Venda Full — a devolução vai ao armazém do ML, sem recebimento no CD';

// ── Retirada da devolução na agência (rodada 7) ─────────────────────────────

/** Substatus do envio de DEVOLUÇÃO parado na agência esperando a nossa retirada. */
export const WITHDRAWAL_SUBSTATUS = 'waiting_for_withdrawal';

/**
 * `rmas.return_tracking_method` (tracking_method do envio de devolução no ML)
 * → rótulo. Levantamento de 23/09: as devoluções paradas na agência são PAC
 * (Correios). Método desconhecido aparece cru.
 */
export const RETURN_TRACKING_METHOD_LABELS = {
    PAC: 'PAC (Correios)',
    SEDEX: 'SEDEX (Correios)',
};

/** Rótulo do método de envio da devolução; vazio → ''. */
export function ReturnTrackingMethodLabel(method) {
    const value = String(method || '').trim();
    if (!value) {
        return '';
    }
    return RETURN_TRACKING_METHOD_LABELS[value.toUpperCase()] || value;
}

// ── NF-e de devolução e NF de perda do caso (rodada 7 — spec-nf-xml) ────────

/**
 * As duas marcações fiscais que o Financeiro faz no caso, agora SUBINDO O XML
 * da NF-e emitida (`POST /rmas/:id/fiscal` multipart, arquivo `xml`):
 *   ENTRADA → NF-e de devolução que NÓS emitimos quando o ML não emitiu
 *             (`nf_entry_*`); o XML tem de ser de entrada própria (tpNF=0);
 *   PERDA   → NF de perda quando a devolução chegou ERRADA (`nf_loss_*`);
 *             o XML tem de ser de saída (tpNF=1).
 * Upload PROVISÓRIO até a integração com o Bling/acervo de XML (a chave de
 * acesso é o identificador; `*_source` diz a origem: manual | xml | bling).
 * Na Gestão de Pedidos só se marca com o XML; a tela antiga `/rmas/:id`
 * continua marcando sem arquivo.
 */
export const CASE_FISCAL_TYPES = {
    ENTRADA: {
        label: 'NF-e de devolução',
        action: 'Vincular NF-e de devolução do Bling',
        success: 'NF-e de devolução marcada.',
        hint: 'Selecione a NF-e de devolução já emitida no Bling.',
        fields: {
            at: 'nf_entry_at',
            number: 'nf_entry_number',
            key: 'nf_entry_key',
            source: 'nf_entry_source',
            xml: 'nf_entry_xml_url',
        },
    },
    PERDA: {
        label: 'NF de perda',
        action: 'Vincular NF de perda do Bling',
        success: 'NF de perda marcada.',
        hint: 'Emita a NF de perda no Bling e vincule ao caso.',
        fields: {
            at: 'nf_loss_at',
            number: 'nf_loss_number',
            key: 'nf_loss_key',
            source: 'nf_loss_source',
            xml: 'nf_loss_xml_url',
        },
    },
};

/** Limite do XML no upload (o backend recusa acima de 1 MB). */
export const FISCAL_XML_MAX_BYTES = 1024 * 1024;

/**
 * Dados da NF do caso por tipo: `{ at, number, key, source, xml }` — `at`
 * `undefined` quando o wire ainda não traz o campo (backend antigo).
 */
export function CaseFiscalDoc(occ, type) {
    const meta = CASE_FISCAL_TYPES[type];
    if (!occ || !meta) {
        return null;
    }
    const { fields } = meta;
    return {
        at: occ[fields.at],
        number: occ[fields.number] ?? null,
        key: occ[fields.key] ?? null,
        source: occ[fields.source] ?? null,
        xml: occ[fields.xml] ?? null,
    };
}

/**
 * Devolução física vindo para o NOSSO endereço — espelho do `returnToUs` de
 * `RmaQueuePredicates` (tags `nfe_devolucao_para_emitir` e
 * `devolucao_para_retirar`): destino do vendedor (`seller_address`) vale
 * mesmo em venda Full; sem destino no wire, só não-Full e não decidida no
 * armazém do ML (`ml_resolution_reason` warehouse_decision).
 */
export function IsReturnToUs(occ) {
    if (!occ) {
        return false;
    }
    const hasReturn =
        Boolean(occ.has_return) ||
        Boolean(occ.return_id) ||
        (Array.isArray(occ.kinds) && occ.kinds.includes('devolucao'));
    if (!hasReturn) {
        return false;
    }
    if (occ.return_destination) {
        return occ.return_destination === 'seller_address';
    }
    const physical = typeof occ.physical_flow === 'boolean' ? occ.physical_flow : !occ.is_full;
    return physical && occ.ml_resolution_reason !== 'warehouse_decision';
}

/**
 * O ML emitiu a NF-e de devolução deste caso? Lê os campos PLANOS
 * `ml_return_invoice_*` da ocorrência do pedido (`GET /pedidos/:id`); o caso
 * de `GET /rmas/:id` passa antes por `FlattenReturnInvoice`.
 */
export function HasMlReturnInvoice(occ) {
    return Boolean(occ && (occ.ml_return_invoice_id || occ.ml_return_invoice_number));
}

/**
 * O DTO do caso (`GET /rmas/:id`) traz a NF-e de devolução do ML aninhada —
 * `ml_return_invoice: { id, number, key, danfe_url } | null` —, a ocorrência
 * do pedido traz os campos planos `ml_return_invoice_id/number/key`. Os
 * componentes fiscais leem o formato plano: esta função achata o do caso
 * (objeto sem o aninhado volta como veio).
 */
export function FlattenReturnInvoice(rma) {
    const invoice = rma?.ml_return_invoice;
    if (!invoice || typeof invoice !== 'object') {
        return rma;
    }
    return {
        ...rma,
        ml_return_invoice_id: invoice.id ?? null,
        ml_return_invoice_number: invoice.number ?? null,
        ml_return_invoice_key: invoice.key ?? null,
    };
}

/**
 * Status do envio de devolução em que a devolução NÃO aconteceu — o
 * comprador não postou (cancelada/expirada). Espelho de
 * `RETURN_INVOICE_SKIPPED_STATUSES` do backend (fora da checagem da NF-e e
 * da tag `nfe_devolucao_para_emitir`).
 */
export const RETURN_SKIPPED_STATUSES = ['cancelled', 'expired'];

/**
 * A devolução foi cancelada/expirada? Lê `return_shipment_status` (caso,
 * `GET /rmas/:id`) ou `return_status` (ocorrência do pedido).
 */
export function IsReturnSkipped(occ) {
    const status = occ?.return_shipment_status ?? occ?.return_status ?? null;
    return RETURN_SKIPPED_STATUSES.includes(status);
}

/**
 * Cabe "Marcar NF-e de devolução emitida"? Devolução para nós que aconteceu
 * (não cancelada/expirada — `IsReturnSkipped`, como o predicado
 * `nfe_devolucao_emitir`), sem NF-e do ML e ainda não marcada (`nf_entry_at`
 * null — `undefined` = backend antigo sem o campo → não oferece). Antes da
 * checagem no ML (`ml_return_invoice_checked_at` null) a tag não acende, mas
 * a marcação já pode ser feita (de propósito).
 */
export function NeedsReturnNfe(occ) {
    return IsReturnToUs(occ) && !IsReturnSkipped(occ) && !HasMlReturnInvoice(occ) && occ.nf_entry_at === null;
}

/**
 * Cabe "Marcar NF de perda emitida"? Chegou ERRADO — inspeção com problema
 * (`inspection_issue`) — OU o supervisor definiu o destino DESCARTE (ajuste 2
 * do dono — mesma regra de `fiscal.nf_perda_pendente` no backend), e sem NF
 * de perda. Avaria sem problema não é perda (vai para o fabricante). Sem
 * `inspection_issue` no wire, `lossTagged` (o pedido tem a tag
 * `nf_perda_para_emitir`) decide para caso já inspecionado.
 */
export function NeedsLossNf(occ, { lossTagged = false } = {}) {
    if (!occ || occ.nf_loss_at !== null) {
        return false;
    }
    if (occ.loss_requested_at) {
        return true;
    }
    if (occ.inspection_destination === 'DESCARTE') {
        return true;
    }
    if (typeof occ.inspection_issue === 'boolean') {
        return occ.inspection_issue;
    }
    return Boolean(lossTagged && occ.inspected_at);
}

// ── Sincronizar tudo (`/pedidos` — dono, 2026-09-23) ─────────────────────────

/**
 * Os 6 jobs que `POST /pedidos/sync-all` dispara em sequência, no background
 * → rótulo pt-BR. O status (`GET /pedidos/sync-all/status`) já traz `label`
 * por job; este mapa é só a rede quando vier sem (job fora daqui aparece com
 * o nome cru).
 */
export const SYNC_ALL_JOB_LABELS = {
    sync_sales_orders_worker: 'Pedidos novos',
    sync_ml_order_status_updates_worker: 'Mudanças de status',
    sync_ml_claims_delta_worker: 'Reclamações e mediações',
    sync_atendimento_unread_worker: 'Mensagens',
    sync_shipment_history_worker: 'Envios',
    sync_return_tracking_worker: 'Envio e NF-e de devolução',
};

/** Rótulo de um job do "Sincronizar tudo": `label` do wire → mapa → nome cru. */
export function SyncAllJobLabel(job) {
    return job?.label || SYNC_ALL_JOB_LABELS[job?.name] || String(job?.name || '—');
}

/** Status de `system_jobs_state` que o front distingue. */
export const SYNC_JOB_RUNNING = 'RUNNING';
export const SYNC_JOB_FAILED = 'FAILED';

/**
 * Estado de cada job DENTRO da rodada (`jobs[].round_state` do status) →
 * texto do title da "Última sincronização" enquanto a rodada corre.
 */
export const SYNC_ROUND_STATE_LABELS = {
    queued: 'na fila',
    running: 'rodando agora',
    waiting: 'já rodando pelo agendador',
    done: 'concluída',
    failed: 'falhou',
    cancelled: 'não rodou (servidor reiniciando)',
};

/** Acompanhamento do status enquanto a sincronização roda (spec: 5 s). */
export const SYNC_ALL_POLL_MS = 5000;

// ── Triagem do caso e apoio do financeiro (rodada 5 — 2026-09-29) ───────────
// Todo caso de reclamação/devolução/mediação elegível (fora cancelamento de
// compra; aberto a partir de `triagem_data_corte`) passa por: o ATENDIMENTO
// tria ("Triagem concluída") → o SUPERVISOR (`rmas:resolve`) decide Resolvido
// / Não resolvido / Apoio do financeiro. "Não resolvido" mantém o caso aberto
// (volta ao Atendimento, que pode triar de novo). "Apoio do financeiro" pede
// uma verificação ao financeiro (observação obrigatória); o financeiro fecha
// o apoio DELE com uma resposta e o caso volta ao supervisor. Wire do caso
// (`review` + `finance_support`, snake_case) — igual no `/rmas/:id` e na
// ocorrência do pedido.

/** Valores de `review.status` (null = para triar). */
export const CASE_REVIEW_STATUSES = ['TRIADO', 'RESOLVIDO', 'NAO_RESOLVIDO', 'APOIO_FINANCEIRO'];

/** Decisões do supervisor, na ordem dos botões. */
export const CASE_REVIEW_DECISIONS = ['RESOLVIDO', 'NAO_RESOLVIDO', 'APOIO_FINANCEIRO'];

/** Rótulo de cada decisão (botão e "Supervisor: …"). */
export const CASE_REVIEW_DECISION_LABELS = {
    RESOLVIDO: 'Resolvido',
    NAO_RESOLVIDO: 'Não resolvido',
    APOIO_FINANCEIRO: 'Apoio do financeiro',
};

/** Limite das notas da triagem/decisão/resposta (o backend recusa acima). */
export const CASE_REVIEW_NOTE_MAX = 2000;

/** Mínimo da observação obrigatória (apoio do financeiro, resposta, pendência). */
export const REQUIRED_NOTE_MIN = 3;

/**
 * Estado do caso na triagem, a partir do wire → `{ key, label, hint, tone }`.
 * `tone` = chave de `TONE_CLASSES` (nunca red: não é urgência). Sem `review`
 * no wire (backend antigo) → null; `eligible === false` → estado 'fora'.
 */
export function CaseReviewState(review, financeSupport = null) {
    if (!review || typeof review !== 'object') {
        return null;
    }
    if (review.finished_at) {
        return financeSupport?.status === 'ABERTO'
            ? { key: 'apoio_aberto', label: 'Atendimento finalizado — financeiro pendente', hint: null, tone: 'cyan' }
            : { key: 'resolvido', label: 'Atendimento finalizado', hint: null, tone: 'emerald' };
    }
    if (review.eligible === false) {
        return {
            key: 'fora',
            label: 'Fora da triagem',
            hint: 'cancelamento de compra ou caso aberto antes da data de corte',
            tone: 'neutral',
        };
    }
    const status = review.status ?? null;
    if (status === null) {
        return { key: 'para_triar', label: 'Para triar', hint: 'o Atendimento tria primeiro', tone: 'sky' };
    }
    if (status === 'TRIADO') {
        return { key: 'triado', label: 'Triado — aguardando supervisor', hint: null, tone: 'sky' };
    }
    if (status === 'NAO_RESOLVIDO') {
        return {
            key: 'nao_resolvido',
            label: 'Não resolvido',
            hint: 'o caso continua aberto — volta para o Atendimento',
            tone: 'amber',
        };
    }
    if (status === 'APOIO_FINANCEIRO') {
        if (financeSupport?.status === 'CONCLUIDO') {
            return {
                key: 'financeiro_respondeu',
                label: 'Financeiro respondeu — aguardando supervisor',
                hint: null,
                tone: 'sky',
            };
        }
        return { key: 'apoio_aberto', label: 'Apoio do financeiro — aguardando', hint: null, tone: 'cyan' };
    }
    if (status === 'RESOLVIDO') {
        return { key: 'resolvido', label: 'Resolvido', hint: null, tone: 'emerald' };
    }
    return { key: String(status), label: String(status), hint: null, tone: 'neutral' };
}

// ── Status do financeiro por pedido (rodada 5 — 2026-09-29) ─────────────────
// Double check manual do financeiro, INDEPENDENTE de alguém ter aberto o
// pedido e SEPARADO da conciliação do pedido. Wire `finance_review` =
// `{ status, note, updated_at, updated_by_name }` (sem linha = NAO_VISTO);
// `null` quando o setor financeiro não é visível para o usuário.

/** Status, na ordem dos botões; `filter` = valor minúsculo do filtro `?finance_review=`. */
export const FINANCE_REVIEW_META = {
    NAO_VISTO: { label: 'Não visto', filter: 'nao_visto', tone: 'neutral' },
    VISTO: { label: 'Visto', filter: 'visto', tone: 'emerald' },
    PENDENCIA: { label: 'Pendência', filter: 'pendencia', tone: 'amber' },
};

export const FINANCE_REVIEW_STATUSES = Object.keys(FINANCE_REVIEW_META);

/** Valores do filtro da lista (CSV no wire, semântica OU), na ordem dos chips. */
export const FINANCE_REVIEW_FILTERS = FINANCE_REVIEW_STATUSES.map((status) => FINANCE_REVIEW_META[status].filter);

/** Meta do status; desconhecido volta cru, neutro; vazio → Não visto. */
export function FinanceReviewMeta(status) {
    const key = String(status || 'NAO_VISTO');
    return FINANCE_REVIEW_META[key] || { label: key, filter: key.toLowerCase(), tone: 'neutral' };
}

/**
 * Title dos chips com `detail` do apoio/pendência (paridade com o `detail`
 * da retirada): `apoio_financeiro_solicitado` → "pedido: …" + quem/quando;
 * `pendencia_financeira` → "nota: …" + quem/quando. Outras tags → [].
 * `formatInstant` = formatador de instante (o do chip, sem ano).
 */
export function TagSupportTitleParts(tag, formatInstant = (value) => value) {
    const key = tag && typeof tag === 'object' ? tag.key : tag;
    const detail = tag && typeof tag === 'object' ? tag.detail : null;
    if (!detail || typeof detail !== 'object') {
        return [];
    }
    const text = (value) => (value === null || value === undefined ? '' : String(value).trim());
    if (key === 'apoio_financeiro_solicitado') {
        const by = text(detail.requested_by);
        const at = detail.requested_at ? formatInstant(detail.requested_at) : '';
        return [
            text(detail.request) ? `pedido: ${text(detail.request)}` : null,
            by || at ? `pedido por ${[by, at].filter(Boolean).join(' em ')}` : null,
        ].filter(Boolean);
    }
    if (key === 'pendencia_financeira') {
        const by = text(detail.updated_by);
        const at = detail.updated_at ? formatInstant(detail.updated_at) : '';
        return [
            text(detail.note) ? `nota: ${text(detail.note)}` : null,
            by || at ? `marcada por ${[by, at].filter(Boolean).join(' em ')}` : null,
        ].filter(Boolean);
    }
    return [];
}

// ── Regras agrupadas (página de Regras, rodada 5 — 2026-09-29) ───────────────

/**
 * Seções próprias da página de Regras, na ordem. As keys aqui saem das
 * seções genéricas (SLA de resposta / Data de corte). Unidades novas do
 * wire: 'count' (casos) e 'days' (dias) — inteiros, não anuláveis.
 */
export const RULE_GROUPS = [
    {
        key: 'triagem',
        title: 'Triagem',
        description:
            'Todo caso de reclamação, devolução ou mediação passa pela triagem do Atendimento e pela decisão do supervisor. Cancelamentos de compra ficam fora.',
        keys: ['triagem_data_corte', 'sla_triagem_minutos', 'sla_decisao_minutos'],
    },
    {
        key: 'financeiro',
        title: 'Financeiro',
        description: 'Apoio pedido pelo supervisor no caso e pendência marcada no status do financeiro do pedido.',
        keys: ['sla_apoio_financeiro_minutos', 'sla_pendencia_financeira_minutos'],
    },
    {
        key: 'alerta_comprador',
        title: 'Alerta do comprador',
        description:
            'Aviso na aba Comprador e no cabeçalho do pedido quando o mesmo comprador (mesma conta do ML) acumula reclamações. Não bloqueia nada.',
        keys: ['alerta_comprador_casos', 'alerta_comprador_dias'],
    },
];

/** Keys que moram numa seção própria (fora das seções genéricas). */
export const GROUPED_RULE_KEYS = RULE_GROUPS.flatMap((group) => group.keys);

/** Unidades inteiras novas → rótulo curto e por extenso. */
export const RULE_UNIT_LABELS = {
    count: { short: 'casos', long: 'casos' },
    days: { short: 'dias', long: 'dias' },
};

// ── Histórico do comprador (rodada 5 — 2026-09-29) ───────────────────────────

/**
 * `cases_by_type` do `/pedidos/:id/comprador` → rótulo no plural (singular
 * vem de `ML_CLAIM_TYPE_LABELS`). Tipo fora daqui aparece com o rótulo do
 * catálogo do ML ou cru.
 */
export const BUYER_CASE_TYPE_PLURALS = {
    mediations: 'reclamações',
    returns: 'devoluções',
    return: 'devoluções',
    fulfillment: 'reclamações Full',
    ml_case: 'casos do ML',
    cancel_sale: 'cancelamentos de venda',
    change: 'trocas',
    service: 'serviços',
};
